// Map picture producer (the full Map, playermap.js): the CLASS MAP painted on the device
// (mapclass.js: the colours, the light, the zoom rule; PROTOCOL.md "Map pack") — the bake ships
// no Map imagery. Image row 0 is the NORTH (z-min) edge, so `drawImage(img, x0, z0, w, h)` in
// metres is right inside the map's world transform.
//
//   inputs    MapClass.start() (the PlayerMap constructor, boot): surface/coarse.png (4 m class
//             grid, meta.surface.coarse) through the asset queue class `map` BEFORE any bulk
//             family (surface.js decodeImage: fetch + decode off the paint path, the R channel),
//             surface/genome.json (class `map`; the ground painter's fetch of it dedupes in the
//             HTTP cache) and the 16 m posts of the GROUND-M skin (mapheights.js MapHeights: one
//             groundm level, the coarsest with tol <= 4 m — L4 live — through lookup.js groundAt,
//             class `map`; no terrain level). A LOOK change against the old 5x5-min terrain
//             envelope (L5): the hillshade now reads the TIN at tol 4 m — the owner's look decides.
//             s2col (meta.surface.s2col) at the refine / the first tile;
//             the residual tiles (meta.surface.residual) a class tile overlaps, at that tile.
//   underlay  the FIRST paint the moment the three land: stride FIRST_STRIDE (16 m/px, ~0.45
//             Mpx); then ONE refine to the device's texel (tunables map.underlay_m: phone 8 m,
//             other 4 m) with the S2 tint, ONLY while the full Map is open: refine(uM, live) —
//             pause() (the Map closed) stops it at the next band, the next open resumes it from
//             that row. onImg(img, wM, hM) per underlay (the Map's _img).
//   tiles     tile(t) -> a 512² bitmap of one 512 m class tile (surface/tiles, class `map`;
//             the ground painter's fetches of them dedupe) with the residual + S2 tint;
//             TileCache: nearest-to-centre first, ≤ MAX_LOADS in flight, LRU of ≈ CAP bitmaps
//             (a tile in the current want-set is never evicted; an evicted ImageBitmap is
//             close()d); a failed tile is not retried this session. drawTiles: the loaded
//             ones over the underlay (a missing tile shows the underlay). THE CLOSE ZOOM (APP_PREPAINT §3):
//             when the pre-paint hits (paintkey.js paintHit) a tile is the shipped slot of the level of
//             its size (surface/paint/L2: the 3D ground's own picture, flipped to row 0 north), class `map`;
//             a miss / an absent slot paints the class tile.
//   where     a Worker (mapwork.js, __MAP_WORKER_SRC__ inlined by scripts/build-client.js): the
//             main thread only posts the decoded class bytes and receives ImageBitmaps. No
//             Worker: mapclass.js mapCore here, BAND-row bands between sched.js slice() yields.
/* global __MAP_WORKER_SRC__ */
import { fetchAsset } from './assetq.js';
import { decodeImage } from './surface.js';
import { slice } from './sched.js';
import { ANCHOR_DEFAULT } from './ringref.js';
import { mapCore, CAP, MAX_LOADS, FIRST_STRIDE, GRASS_SAT_DEFAULT, TILE_PX } from './mapclass.js';
import { PAINTER_KEY, paintHit } from './paintkey.js';
import { genRaw } from './gensched.js';
import PAL from './painterpal.json';

// ------------------------------------------------------------- the cache
export class TileCache {
  // load(t): a promise of the tile's drawable (null = none); onLoad(t): a tile became drawable
  // (the map bumps its generation + repaints)
  constructor({ load, cap = CAP, maxLoads = MAX_LOADS, onLoad = null }) {
    this.loadFn = load;
    this.cap = cap; this.maxLoads = maxLoads;
    this.onLoad = onLoad;
    this.map = new Map();        // key -> {img, ok, failed, loading}; insertion order = LRU
    this.inFlight = 0;
    this._want = [];             // the latest want-set, nearest first
    this._wantKeys = new Set();
    this.loaded = 0;             // tiles that became drawable (stats)
  }

  // a drawable tile (and mark it recently used) or null
  ready(key) {
    const e = this.map.get(key);
    if (!e || !e.ok) return null;
    this.map.delete(key); this.map.set(key, e);
    return e.img;
  }

  // peek without touching the LRU order
  has(key) { const e = this.map.get(key); return !!(e && e.ok); }

  // the view's tiles (nearest first): queued, loaded up to maxLoads at once
  want(list) {
    this._want = list;
    this._wantKeys = new Set(list.map((t) => t.key));
    this._pump();
  }

  _pump() {
    for (const t of this._want) {
      if (this.inFlight >= this.maxLoads) break;
      if (this.map.has(t.key)) continue;
      this._load(t);
    }
    this._evict();
  }

  _load(t) {
    const e = { img: null, ok: false, failed: false, loading: true };
    this.map.set(t.key, e);
    this.inFlight++;
    const done = (img) => {
      if (!e.loading) return;
      e.loading = false; e.ok = !!img; e.failed = !img;
      this.inFlight--;
      e.img = img;                            // (a loading tile is never evicted)
      if (img) { this.loaded++; if (this.onLoad) this.onLoad(t); }
      this._pump();
    };
    this.loadFn(t).then(done, () => done(null));
  }

  // least recently used first; never a tile in flight or in the want-set
  _evict() {
    if (this.map.size <= this.cap) return;
    for (const [k, e] of this.map) {
      if (this.map.size <= this.cap) break;
      if (e.loading || this._wantKeys.has(k)) continue;
      closeBitmap(e.img);
      this.map.delete(k);
    }
  }
}

// free an ImageBitmap's pixels now (a canvas has no close)
export function closeBitmap(img) { if (img && typeof img.close === 'function') img.close(); }

// the wanted tiles that are loaded, into g (inside the map's world transform, metres; the caller
// clips to the extent); a missing one leaves the underlay → {want, drawn}
export function drawTiles(g, cache, list, T) {
  let drawn = 0;
  for (const t of list) {
    const img = cache.ready(t.key);
    if (!img) continue;
    // a hair of overlap hides the seams the canvas leaves between abutting images
    g.drawImage(img, t.tx * T, t.ty * T, T + T * 0.002, T + T * 0.002);
    drawn++;
  }
  return { want: list.length, drawn };
}

// ------------------------------------------------------------- the producer
// the worker {w, id, wait, dead}, or null (no bundle / Worker / createImageBitmap): mapCore here
function makeWorker() {
  if (typeof __MAP_WORKER_SRC__ === 'undefined' || typeof Worker === 'undefined' || typeof Blob === 'undefined'
    || typeof createImageBitmap === 'undefined') return null;
  try {
    const w = new Worker(URL.createObjectURL(new Blob([__MAP_WORKER_SRC__], { type: 'text/javascript' })));
    const K = { w, id: 0, wait: new Map(), dead: false };
    w.onmessage = (e) => { const f = K.wait.get(e.data.id); if (f) { K.wait.delete(e.data.id); f(e.data); } };
    w.onerror = () => { K.dead = true; for (const f of K.wait.values()) f({ error: 'map worker gone' }); K.wait.clear(); };
    return K;
  } catch { return null; }
}
// the main thread's drawable of painted RGBA: an ImageBitmap, else a canvas
function bitmapMain(rgba, w, h) {
  const id = new ImageData(rgba, w, h);
  if (typeof createImageBitmap === 'function') return createImageBitmap(id);
  const cv = document.createElement('canvas');
  cv.width = w; cv.height = h;
  cv.getContext('2d').putImageData(id, 0, 0);
  return Promise.resolve(cv);
}

export class MapClass {
  // meta: the bake meta (surface, tex.light, tex.s2, lighting, bbox); heights: the
  // MapHeights (mapheights.js: loaded, posts); onImg(img, wM, hM): an underlay is in
  constructor({ baseUrl, meta, heights, onImg }) {
    this.base = baseUrl;
    this.meta = meta;
    this.heights = heights;
    this.onImg = onImg;
    const s = meta.surface;
    if (!(s && s.coarse && s.tiles && s.genome && s.genome.file)) throw new Error('map: meta.surface coarse / tiles / genome missing: rebuild this world (Build)');
    this.grid = s.tiles;
    this.cellM = s.coarse.stride * s.cell_m;     // the coarse texel (m)
    this._K = undefined;          // the worker (null: mapCore on the main thread)
    this._core = null;
    this._ready = null;           // the world + the coarse grid are in the core
    this._first = null;           // ... and the first underlay painted (or failed)
    this._s = Infinity;           // the stride of the underlay shown
    this._busy = false;           // a refine in flight
    this._s2P = null;
    this._resP = new Map();
  }

  // the op -> a promise of its out (the worker's or the main thread's core)
  _ask(m, transfer = []) {
    if (this._K === undefined) {
      this._K = makeWorker();
      if (!this._K) this._core = mapCore({ pause: () => slice(), bitmap: bitmapMain });
    }
    const K = this._K;
    if (!K) return this._core.handle(m);
    if (K.dead) return Promise.reject(new Error('map worker gone'));
    return new Promise((res, rej) => {
      const id = ++K.id;
      K.wait.set(id, (d) => (d.error ? rej(new Error(`map worker: ${d.error}`)) : res(d.out)));
      K.w.postMessage({ ...m, id }, transfer);
    });
  }

  // the inputs + the first underlay (once)
  start() {
    if (this._ready) return this._ready;
    const M = this.meta, s = M.surface, bb = M.bbox, L = M.tex.light, S2 = M.tex.s2, R = s.residual, C = s.s2col;
    const co = decodeImage(`${this.base}/${s.coarse.file}`, 1, 'map');
    const ge = fetchAsset(`${this.base}/${s.genome.file}`, { prio: 'map', as: 'json' });
    const hp = Promise.resolve(this.heights && this.heights.loaded).then(() => this._posts());
    this._ready = Promise.all([co, ge, hp]).then(([c, genome, heights]) => {
      if (c.status !== 200 || c.w !== s.coarse.w || c.h !== s.coarse.h) throw new Error(`map: ${s.coarse.file} status ${c.status} ${c.w}x${c.h}`);
      if (!(genome && genome.classes)) throw new Error(`map: ${s.genome.file} has no classes`);
      const inp = { classes: s.classes, waterId: s.water_id, genome, pal: PAL, light: { sky: L.sky, ground: L.ground, sun: L.sun, white: L.white },
        sunAltDeg: M.lighting.sun_alt_deg, sunAzDeg: M.lighting.sun_az_deg,
        anchor: (S2 && S2.anchor) ?? ANCHOR_DEFAULT, grassSat: (S2 && S2.grass_sat) ?? GRASS_SAT_DEFAULT, heights,
        res: R ? { w: R.w, h: R.h, cell: R.cell_m, x0: R.origin[0] - bb.e0, z0: bb.n1 - R.origin[1] } : null,
        s2: C && C.file ? { cell: C.cell_m, x0: C.origin[0] - bb.e0, z0: bb.n1 - C.origin[1] } : null };
      return Promise.all([this._ask({ op: 'world', inp }, heights ? [heights.a.buffer] : []),   // a copy (_posts)
        this._ask({ op: 'coarse', data: c.data, w: c.w, h: c.h, cell: this.cellM }, [c.data.buffer])]);
    });
    this._first = this._ready.then(() => this._ask({ op: 'underlay', s: FIRST_STRIDE })).then((r) => this._in(r, FIRST_STRIDE))
      .catch((e) => console.error('[map]', e));
    return this._ready;
  }

  // the Map's 16 m posts {a, w, h, cell} (post (i + 0.5) cell over the frame) | null: the
  // MapHeights' own (GROUND-M groundAt, mapheights.js), a COPY — the world op transfers it and
  // the elevation readout (heightAt) keeps reading the original
  _posts() {
    const P = this.heights && this.heights.posts;
    return P ? { a: P.a.slice(), w: P.w, h: P.h, cell: P.cell } : null;
  }

  _in(r, s) {
    if (!r || r.paused || !r.img) return false;
    if (s > this._s) { if (r.img.close) r.img.close(); return true; }   // a finer one is already in
    this._s = s;
    this.onImg(r.img, r.w * r.texel, r.h * r.texel);
    return true;
  }

  // the S2 colour into the core (once; none: resolved)
  _s2() {
    if (!this._s2P) {
      const C = this.meta.surface.s2col;
      this._s2P = !(C && C.file) ? Promise.resolve() : this._ready.then(() => decodeImage(`${this.base}/${C.file}`, 3, 'map'))
        .then((d) => (d.status === 200 ? this._ask({ op: 's2', data: d.data, w: d.w, h: d.h }, [d.data.buffer]) : null));
    }
    return this._s2P;
  }

  // the residual tiles class tile (tx, ty) reads (its 512 m + one cell), into the core
  _res(tx, ty) {
    const R = this.meta.surface.residual;
    if (!R) return Promise.resolve();
    const bb = this.meta.bbox, x0 = R.origin[0] - bb.e0, z0 = bb.n1 - R.origin[1], T = this.grid.tile_m, rm = R.tile_px * R.cell_m;
    const lo = (v, o) => Math.floor((v - o - R.cell_m) / rm), hi = (v, o) => Math.floor((v - o + R.cell_m) / rm);
    const ps = [];
    for (let j = Math.max(0, lo(ty * T, z0)); j <= Math.min(R.ny - 1, hi((ty + 1) * T, z0)); j++) {
      for (let i = Math.max(0, lo(tx * T, x0)); i <= Math.min(R.nx - 1, hi((tx + 1) * T, x0)); i++) {
        const k = `${i}_${j}`;
        let p = this._resP.get(k);
        if (!p) {
          p = decodeImage(`${this.base}/${R.dir}/${k}.${R.ext}`, 3, 'map')
            .then((d) => (d.status === 200 ? this._ask({ op: 'res', tx: i, ty: j, px: R.tile_px, data: d.data, w: d.w, h: d.h }, [d.data.buffer]) : null));
          this._resP.set(k, p);
        }
        ps.push(p);
      }
    }
    return Promise.all(ps);
  }

  // the device underlay (texel uM m) while live() (the full Map open); resumes where a pause left it
  refine(uM, live) {
    const s = Math.max(1, Math.round(uM / this.cellM));
    if (!this._first || this._busy || s >= this._s || !live()) return;
    this._busy = true;
    this._first.then(() => this._s2()).then(() => (live() ? this._ask({ op: 'underlay', s, keep: true }) : { paused: true }))
      .then((r) => { this._busy = false; this._in(r, s); }, (e) => { this._busy = false; console.error('[map]', e); });
  }

  // the full Map closed: a running refine stops at its next band (kept for the next open)
  pause() { if (this._busy) this._ask({ op: 'pause' }).catch(() => {}); }

  // the shipped level whose slot is a class tile (128 · 2^j m = tile_m; APP_PREPAINT §3) when the pre-paint hits
  // (paintkey.js paintHit, the ground painter's rule), else -1
  _slotLv() {
    const P = this.meta.surface.paint, j = Math.log2(this.grid.tile_m / 128);
    return P && Number.isInteger(j) && P.levels.includes(j) && paintHit(P, PAINTER_KEY, genRaw()) ? j : -1;
  }
  // tile t's shipped slot (surface/paint) as the Map draws it: row 0 north (a slot's row 0 is its south edge) → null = none
  async _slot(t, j) {
    try {
      const r = await fetchAsset(`${this.base}/surface/paint/L${j}/${t.tx}_${t.ty}.webp`, { prio: 'map', status: true });
      if (!r.body) return null;
      const bmp = await createImageBitmap(new Blob([r.body], { type: 'image/webp' }), { colorSpaceConversion: 'none', imageOrientation: 'flipY' });
      if (bmp.width === TILE_PX && bmp.height === TILE_PX) return bmp;
      bmp.close();
    } catch (e) { console.warn('[map] shipped slot', t.tx, t.ty, e && e.message); }
    return null;
  }

  // one class tile t {tx, ty} → its drawable (null = none): the CLOSE ZOOM draws the 3D ground's own picture, the
  // shipped slot of that level, when the pre-paint hits; a miss / an absent slot paints the class tile here
  async tile(t) {
    await this._ready;
    const j = this._slotLv(), sl = j >= 0 ? await this._slot(t, j) : null;
    if (sl) return sl;
    const T = this.grid;
    const [d] = await Promise.all([decodeImage(`${this.base}/${T.dir}/${t.tx}_${t.ty}.${T.ext}`, 1, 'map'), this._s2(), this._res(t.tx, t.ty)]);
    if (d.status !== 200) return null;
    const r = await this._ask({ op: 'tile', tx: t.tx, ty: t.ty, tileM: T.tile_m, data: d.data, w: d.w, h: d.h }, [d.data.buffer]);
    return r.img;
  }
}
